import { shallowRef } from 'vue';
import {
  parseAppConfigDocument,
  type AppConfigDocument,
} from '@alfrec/compatibility-runtime/contracts';

import { syncOrigin } from './config';
import { clientMetadataHeaders } from './localdb/clientMetadata';

const STORAGE_KEY = 'alfrec.app-config.v1';

const canonicalDocumentFromResponse = (input: unknown): AppConfigDocument => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return parseAppConfigDocument(input);
  }
  const {
    requestId: _requestId,
    source: _source,
    ...document
  } = input as Record<string, unknown>;
  return parseAppConfigDocument(document);
};

export const readCachedAppConfig = (): AppConfigDocument | null => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parseAppConfigDocument(JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
};

const persistAppConfig = (document: AppConfigDocument): void => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(document));
};

const metadataHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  ...clientMetadataHeaders(),
});

/// Latest usable app-config document (cached immediately, refreshed by
/// fetchAppConfig). Null only before the very first successful fetch.
export const appConfigDocument = shallowRef<AppConfigDocument | null>(
  readCachedAppConfig(),
);

export const fetchAppConfig = async (): Promise<AppConfigDocument | null> => {
  const cached = readCachedAppConfig();
  const headers = metadataHeaders();
  if (cached?.revision) {
    headers['If-None-Match'] = `"${cached.revision}"`;
  }
  try {
    const response = await fetch(`${syncOrigin}/v1/app-config`, {
      method: 'GET',
      cache: 'no-store',
      headers,
    });
    if (response.status === 304) return cached;
    if (!response.ok) return cached;
    const document = canonicalDocumentFromResponse(await response.json());
    persistAppConfig(document);
    appConfigDocument.value = document;
    return document;
  } catch {
    return cached;
  }
};
